/**
 * @module useSalesMenu
 * Shared state for the Sales menu area: coverage reporting and order import.
 *
 * All refs live at module scope (outside the exported function) so they are
 * true singletons — every component that calls useSalesMenu() reads and
 * writes the same state. This lets SalesMenu.vue open the dialogs and
 * CoverageReportDialog / ImportOrderDetailsDialog display and mutate them
 * without prop drilling or events.
 *
 * Platform auto-fetch state uses the "talabat" prefix for historical reasons;
 * the same from/to date range is reused for all platforms that support it
 * (Talabat, Noon Payments). Deliverect and Keeta use date presets
 * instead and are listed in PRESET_PLATFORMS.
 */

import { ref, computed } from "vue";
import api from "@/utils/api.js";
import { toLocalYMD } from "@/utils/dateLocal.js";
import salesCoverageApi from "@/api/salesCoverage.js";

// ── Coverage dialog state ────────────────────────────────────────────────────

const showCoverageDialog = ref(false);
const coverageLoading = ref(false);
const coverageError = ref(null);
const coverageData = ref(null);
const coverageStartDate = ref(null);
const coverageEndDate = ref(null);
const selectedDay = ref(null);
const showMissingDialog = ref(false);

// ── Platform auto-fetch state ────────────────────────────────────────────────

// Default to yesterday because today's data may not be fully entered yet
function yesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toLocalYMD(d);
}

const talabatFetchFrom = ref(yesterday());
const talabatFetchTo = ref(yesterday());
const talabatFetchLoading = ref(false);
const talabatFetchError = ref(null);
// Shown while the long-running platform fetch is in progress (login + download can take 30–60s)
const fetchProgressMessage = ref("");

// ── Import dialog state ──────────────────────────────────────────────────────

const showImportDialog = ref(false);
const importFile = ref(null);
const importPlatform = ref(null);
const importLoading = ref(false);
const importError = ref(null);
const importData = ref(null);
const importSelectedRows = ref([]);
const importFilterStartDate = ref(null);
const importFilterEndDate = ref(null);
const importFilterCity = ref(null);
const importFilterLocation = ref(null);
const importFilterBrand = ref(null);
const importSaving = ref(false);
const importSaveResult = ref(null);
// Date range for virtual record generation (Direct Sales upload)
const importDateFrom = ref(null);
const importDateTo = ref(null);

// Manual conflict detection: records that would overwrite hand-entered data
const manualConflicts = ref([]);
const showManualConflictDialog = ref(false);

// Incrementing this triggers watchers in sales views to reload their data
const salesDataRefreshTrigger = ref(0);

// Cross-component callback: SalesRawData registers its CSV export function here
// so SalesMenu can trigger it without a direct parent–child relationship
const exportCsvHandler = ref(null);

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDateNDaysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0];
}

const currentDate = new Date().toISOString().split("T")[0];

// ── Composable ───────────────────────────────────────────────────────────────

export function useSalesMenu() {
  // ── Coverage functions ─────────────────────────────────────────────────────

  function openCoverageDialog() {
    coverageError.value = null;
    coverageData.value = null;
    // Default to last 7 days so the dialog shows useful data immediately on open
    coverageStartDate.value = getDateNDaysAgo(7);
    coverageEndDate.value = currentDate;
    showCoverageDialog.value = true;
  }

  function closeCoverageDialog() {
    showCoverageDialog.value = false;
  }

  async function fetchCoverage() {
    coverageLoading.value = true;
    coverageError.value = null;
    coverageData.value = null;

    try {
      // Third argument `true` requests business-rule-based expected counts
      coverageData.value = await salesCoverageApi.getCoverageReport(
        coverageStartDate.value,
        coverageEndDate.value,
        true
      );
    } catch (err) {
      coverageError.value = err.response?.data?.error || err.message || "Failed to fetch coverage data";
    } finally {
      coverageLoading.value = false;
    }
  }

  function showMissingDetails(item) {
    selectedDay.value = item;
    showMissingDialog.value = true;
  }

  function closeMissingDialog() {
    showMissingDialog.value = false;
  }

  // ── Import functions ───────────────────────────────────────────────────────

  async function openImportDialog(fetchOptions) {
    // Reset all import state so previous run's data doesn't leak into the new one
    importFile.value = null;
    importPlatform.value = null;
    importError.value = null;
    importData.value = null;
    importSelectedRows.value = [];
    importFilterStartDate.value = null;
    importFilterEndDate.value = null;
    importFilterCity.value = null;
    importFilterLocation.value = null;
    importFilterBrand.value = null;
    importSaving.value = false;
    importSaveResult.value = null;
    importDateFrom.value = null;
    importDateTo.value = null;
    talabatFetchFrom.value = yesterday();
    talabatFetchTo.value = yesterday();
    talabatFetchLoading.value = false;
    talabatFetchError.value = null;
    showImportDialog.value = true;
    if (fetchOptions) {
      await fetchOptions();
    }
  }

  function closeImportDialog() {
    showImportDialog.value = false;
  }

  async function processImportFile() {
    const fileVal = importFile.value;
    if (!fileVal || (Array.isArray(fileVal) && fileVal.length === 0) || !importPlatform.value) return;

    importLoading.value = true;
    importError.value = null;
    importData.value = null;
    importSaveResult.value = null;

    try {
      const formData = new FormData();
      const files = Array.isArray(importFile.value) ? importFile.value : [importFile.value];
      for (const f of files) {
        formData.append("file", f);
      }
      formData.append("platform", importPlatform.value);

      // Pass optional date range for virtual record generation (all platforms)
      if (importDateFrom.value) formData.append("fromDate", importDateFrom.value);
      if (importDateTo.value) formData.append("toDate", importDateTo.value);

      // Direct Sales uses a different endpoint with a simpler parser
      const endpoint = importPlatform.value === "Direct Sales"
        ? "/api/accounting/sales/import/direct-sales"
        : "/api/accounting/sales/import/order-details";

      const response = await api.accountingApi.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      importData.value = response.data;
    } catch (err) {
      importError.value = err.response?.data?.error || err.message || "Failed to process file";
    } finally {
      importLoading.value = false;
    }
  }

  // ── Platform auto-fetch ────────────────────────────────────────────────────

  // Keeta endpoint exists but auto_download_enabled=0 in the DB, so it never
  // appears in the UI. The backend code is preserved for future attempts once
  // bot detection is solved.
  const PLATFORM_FETCH_ENDPOINTS = {
    Talabat: "/api/accounting/sales/import/talabat-fetch",
    "Noon Payments": "/api/accounting/sales/import/noon-payments-fetch",
    Deliverect: "/api/accounting/sales/import/deliverect-fetch",
    Keeta: "/api/accounting/sales/import/keeta-fetch",
  };

  // These platforms expose date presets (e.g. "Last Week") instead of
  // arbitrary from/to dates because their UIs don't support custom ranges
  const PRESET_PLATFORMS = new Set(["Deliverect", "Keeta"]);

  async function fetchFromPlatform(platform, options = {}) {
    const usesPreset = PRESET_PLATFORMS.has(platform);
    if (!usesPreset && (!talabatFetchFrom.value || !talabatFetchTo.value)) return;
    if (!platform) return;

    const endpoint = PLATFORM_FETCH_ENDPOINTS[platform];
    if (!endpoint) {
      talabatFetchError.value = `No auto-download endpoint configured for ${platform}`;
      return;
    }

    talabatFetchLoading.value = true;
    talabatFetchError.value = null;
    fetchProgressMessage.value = `Connecting to ${platform}...`;
    importData.value = null;
    importSaveResult.value = null;

    // Progress messages are shown on a timer because the backend fetch can take
    // 30–90 seconds (login + multi-restaurant downloads). These messages reassure
    // the user the request is still running.
    const progressSteps = [
      { after: 5, message: `Logging in to ${platform}...` },
      { after: 12, message: `Downloading report data...` },
      { after: 30, message: `Still fetching data from multiple restaurants...` },
      { after: 60, message: `Almost there, please wait...` },
    ];
    const startTime = Date.now();
    const progressTimer = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      for (let i = progressSteps.length - 1; i >= 0; i--) {
        if (elapsed >= progressSteps[i].after) {
          fetchProgressMessage.value = progressSteps[i].message;
          break;
        }
      }
    }, 1000);

    try {
      const requestBody = usesPreset
        ? { datePreset: options.datePreset || "Last Week" }
        : { from: talabatFetchFrom.value, to: talabatFetchTo.value };

      const response = await api.accountingApi.post(endpoint, requestBody, {
        timeout: 120000, // 2 minutes — Deliverect Puppeteer automation can be slow
      });

      importData.value = response.data;
      // Store the format name (e.g. "Noon Payments") so saveSelectedRecords can
      // send it as `source` for audit logging. The per-record platform field
      // ("Noon") is already set by the backend parser.
      importPlatform.value = platform;
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errorCode === "CREDENTIALS_NOT_CONFIGURED") {
        talabatFetchError.value = `${platform} credentials are not configured. Go to Settings > Platform Credentials to set them up.`;
      } else if (errorData?.errorCode?.endsWith("_LOGIN_FAILED")) {
        talabatFetchError.value = `${platform} login failed. Check your credentials in Settings > Platform Credentials.`;
      } else {
        talabatFetchError.value = errorData?.error || err.message || `Failed to fetch ${platform} report`;
      }
    } finally {
      clearInterval(progressTimer);
      fetchProgressMessage.value = "";
      talabatFetchLoading.value = false;
    }
  }

  // ── Save selected records ──────────────────────────────────────────────────

  /**
   * Save selected import records to the database via bulk import endpoint.
   *
   * Import conflict handling for manually-entered records:
   * ──────────────────────────────────────────────────────
   * When the backend detects that an imported record would overwrite an existing
   * record whose source is 'manual' (i.e. hand-entered by a user), it does NOT
   * silently update it. Instead it collects those records into a `manualConflicts`
   * array in the response.
   *
   * Flow:
   * 1. Frontend POSTs selected records → backend processes them.
   * 2. Non-manual duplicates are updated normally. New records are inserted.
   * 3. Manual duplicates are skipped and returned in `response.manualConflicts`.
   * 4. If manualConflicts is non-empty, we store them in `manualConflicts` ref
   *    and the ImportOrderDetailsDialog shows a confirmation dialog.
   * 5. User can "Skip" (keep manual records) or "Overwrite All" which calls
   *    `forceOverwriteManualRecords()` — a second POST with the conflict records
   *    and `forceOverwriteManual: true` to bypass the check.
   *
   * This two-phase approach avoids data loss from accidental overwrites of
   * carefully hand-entered data while keeping the normal import flow unchanged
   * for platform-sourced records.
   *
   * @param {boolean} forceOverwriteManual - If true, tells the backend to
   *   overwrite manual records without conflict detection (used by the retry).
   */
  async function saveSelectedRecords(forceOverwriteManual = false) {
    // When called as a normal save (not a force-overwrite retry), validate selection
    if (!forceOverwriteManual && importSelectedRows.value.length === 0) {
      importSaveResult.value = {
        success: false,
        error: "No records selected",
      };
      return;
    }

    importSaving.value = true;
    importSaveResult.value = null;

    try {
      // Determine which records to send: either the user's selection (normal save)
      // or the previously-collected manual conflicts (force-overwrite retry)
      const sourceRows = forceOverwriteManual
        ? manualConflicts.value
        : importSelectedRows.value;

      const records = sourceRows.map((r) => ({
        date: r.date,
        city: r.city,
        location: r.location,
        brand: r.brand,
        platform: r.platform,
        amount: r.amount,
        orders: r.orders,
        grossAmount: r.grossAmount,
        discount: r.discount,
        commissions: r.commissions,
        onlinePayments: r.onlinePayments,
      }));

      if (records.length === 0 || !records[0].date) {
        importSaveResult.value = {
          success: false,
          error: "Invalid record data - please refresh the page and try again",
        };
        return;
      }

      // Build a descriptive filename for the audit log. Auto-fetched data has
      // no file object so we fall back to a date-range string.
      const fileVal = importFile.value;
      const filename = Array.isArray(fileVal)
        ? fileVal.map((f) => f.name).join(", ")
        : fileVal?.name || (talabatFetchFrom.value ? `${importPlatform.value}_${talabatFetchFrom.value}_to_${talabatFetchTo.value}` : "unknown");

      const response = await api.accountingApi.post("/api/accounting/sales/import/bulk", {
        records,
        source: importPlatform.value,
        filename,
        forceOverwriteManual,
      });

      console.log("Save response:", response.data);
      importSaveResult.value = response.data;

      if (response.data.success) {
        // If the backend reported manual conflicts, store them so the dialog
        // can prompt the user before we clear the selection.
        if (response.data.manualConflicts?.length > 0) {
          manualConflicts.value = response.data.manualConflicts;
          showManualConflictDialog.value = true;
        } else {
          // No conflicts — clear conflicts from any prior save and reset selection
          manualConflicts.value = [];
        }
        importSelectedRows.value = [];
        // Increment the trigger so other open sales views know to reload
        salesDataRefreshTrigger.value++;
      }
    } catch (err) {
      console.error("Save error:", err);
      importSaveResult.value = {
        success: false,
        error: err.response?.data?.error || err.message || "Failed to save records",
      };
    } finally {
      importSaving.value = false;
    }
  }

  /**
   * Re-POST the previously-collected manual conflict records with the
   * forceOverwriteManual flag so the backend overwrites them.
   * Called when the user clicks "Overwrite All" in the conflict dialog.
   */
  async function forceOverwriteManualRecords() {
    showManualConflictDialog.value = false;
    await saveSelectedRecords(true);
    // Clear conflicts after the force-overwrite completes
    manualConflicts.value = [];
  }

  /**
   * Dismiss the manual conflict dialog without overwriting.
   * The manual records are left untouched in the database.
   */
  function skipManualConflicts() {
    showManualConflictDialog.value = false;
    manualConflicts.value = [];
    // Also clear conflicts from the API response object so the warning alert disappears
    if (importSaveResult.value?.manualConflicts) {
      importSaveResult.value.manualConflicts = [];
    }
  }

  // ── Import computed properties ─────────────────────────────────────────────

  const importDateRange = computed(() => {
    if (!importData.value?.data?.length) return { min: null, max: null };
    const dates = importData.value.data.map((r) => r.date).sort();
    return { min: dates[0], max: dates[dates.length - 1] };
  });

  const filteredImportData = computed(() => {
    if (!importData.value?.data) return [];
    // Add a stable numeric id so Vuetify's v-data-table can track row identity
    let data = importData.value.data.map((item, index) => ({ ...item, id: index }));

    if (importFilterStartDate.value) {
      data = data.filter((r) => r.date >= importFilterStartDate.value);
    }
    if (importFilterEndDate.value) {
      data = data.filter((r) => r.date <= importFilterEndDate.value);
    }
    if (importFilterCity.value) {
      data = data.filter((r) => r.city === importFilterCity.value);
    }
    if (importFilterLocation.value) {
      data = data.filter((r) => r.location === importFilterLocation.value);
    }
    if (importFilterBrand.value) {
      data = data.filter((r) => r.brand === importFilterBrand.value);
    }

    // Most recent dates first so the user reviews the latest data at the top
    data.sort((a, b) => b.date.localeCompare(a.date));

    return data;
  });

  const filteredImportSummary = computed(() => {
    const data = filteredImportData.value;
    const amount = data.reduce((sum, r) => sum + r.amount, 0);
    const grossAmount = data.reduce((sum, r) => sum + (r.grossAmount || 0), 0);
    const discount = data.reduce((sum, r) => sum + (r.discount || 0), 0);
    // discountPct is null when gross is unavailable (e.g. platforms that
    // provide net sales only and have no separate gross/discount columns)
    const discountPct = grossAmount > 0 ? Math.round((discount / grossAmount) * 10000) / 100 : null;
    return {
      days: new Set(data.map((r) => r.date)).size,
      orders: data.reduce((sum, r) => sum + r.orders, 0),
      amount,
      grossAmount,
      discountPct,
      rows: data.length,
    };
  });

  // Shown as chips below the summary card when the import covers 2+ brands,
  // helping the user quickly sanity-check the per-brand split
  const brandBreakdown = computed(() => {
    const data = filteredImportData.value;
    if (!data.length) return [];
    const byBrand = {};
    for (const r of data) {
      if (!byBrand[r.brand]) {
        byBrand[r.brand] = { brand: r.brand, orders: 0, amount: 0, grossAmount: 0, discount: 0 };
      }
      byBrand[r.brand].orders += r.orders;
      byBrand[r.brand].amount += r.amount;
      byBrand[r.brand].grossAmount += (r.grossAmount || 0);
      byBrand[r.brand].discount += (r.discount || 0);
    }
    return Object.values(byBrand)
      .map(b => ({
        ...b,
        discountPct: b.grossAmount > 0 ? Math.round((b.discount / b.grossAmount) * 10000) / 100 : null,
      }))
      .sort((a, b) => a.brand.localeCompare(b.brand));
  });

  // Per-brand, per-location breakdown of the full (unfiltered) import data.
  // Shown as a summary table immediately after processing, before filters.
  const importBreakdown = computed(() => {
    if (!importData.value?.data?.length) return [];
    const map = {};
    for (const r of importData.value.data) {
      // Skip virtual (zero-value) records — they're placeholders, not real data
      if (r.virtual) continue;
      const key = `${r.brand}|${r.location}`;
      if (!map[key]) {
        map[key] = {
          brand: r.brand,
          location: r.location,
          startDate: r.date,
          endDate: r.date,
          days: new Set(),
          orders: 0,
          grossAmount: 0,
          amount: 0,
          discount: 0,
          rows: 0,
        };
      }
      const entry = map[key];
      if (r.date < entry.startDate) entry.startDate = r.date;
      if (r.date > entry.endDate) entry.endDate = r.date;
      entry.days.add(r.date);
      entry.orders += r.orders;
      entry.grossAmount += r.grossAmount || 0;
      entry.amount += r.amount;
      entry.discount += r.discount || 0;
      entry.rows++;
    }
    return Object.values(map)
      .map((e) => ({
        ...e,
        days: e.days.size,
        discountPct: e.grossAmount > 0 ? Math.round((e.discount / e.grossAmount) * 10000) / 100 : null,
      }))
      .sort((a, b) => a.brand.localeCompare(b.brand) || a.location.localeCompare(b.location));
  });

  const selectedImportSummary = computed(() => {
    if (!importSelectedRows.value?.length) return { orders: 0, amount: 0 };
    // Vuetify 3 v-data-table with show-select returns full row objects in v-model
    return {
      orders: importSelectedRows.value.reduce((sum, r) => sum + r.orders, 0),
      amount: importSelectedRows.value.reduce((sum, r) => sum + r.amount, 0),
    };
  });

  // Select across all pages, not just the visible page — useful for large imports
  function selectAllFilteredRecords() {
    importSelectedRows.value = [...filteredImportData.value];
  }

  function deselectAllRecords() {
    importSelectedRows.value = [];
  }

  return {
    // Coverage state
    showCoverageDialog,
    coverageLoading,
    coverageError,
    coverageData,
    coverageStartDate,
    coverageEndDate,
    selectedDay,
    showMissingDialog,
    currentDate,

    // Coverage functions
    openCoverageDialog,
    closeCoverageDialog,
    fetchCoverage,
    showMissingDetails,
    closeMissingDialog,
    getDateNDaysAgo,

    // Import state
    showImportDialog,
    importFile,
    importPlatform,
    importLoading,
    importError,
    importData,
    importSelectedRows,
    importFilterStartDate,
    importFilterEndDate,
    importFilterCity,
    importFilterLocation,
    importFilterBrand,
    importSaving,
    importSaveResult,
    importDateFrom,
    importDateTo,

    // Import computed
    importBreakdown,
    importDateRange,
    filteredImportData,
    filteredImportSummary,
    brandBreakdown,
    selectedImportSummary,

    // Platform fetch
    talabatFetchFrom,
    talabatFetchTo,
    talabatFetchLoading,
    talabatFetchError,
    fetchProgressMessage,
    fetchFromPlatform,

    // Manual conflict state
    manualConflicts,
    showManualConflictDialog,

    // Import functions
    openImportDialog,
    closeImportDialog,
    saveSelectedRecords,
    forceOverwriteManualRecords,
    skipManualConflicts,
    processImportFile,
    selectAllFilteredRecords,
    deselectAllRecords,

    // Refresh trigger for other components
    salesDataRefreshTrigger,

    // Export CSV handler
    exportCsvHandler,
  };
}
